import { z } from 'zod';
import { lenientDateString, lenientNumber } from '../schema-extensions';

// ============================================================================
// CONSTANTS (must be defined before schemas that reference them)
// ============================================================================
const MONTHLY_CHARGE_CONSTANTS = {
  DATE_FORMAT_LENGTH: 10, // 'YYYY-MM-DD'
  MIN_AMOUNT: 0,
};

// ============================================================================
// SCHEMAS
// ============================================================================

/**
 * Lenient schema for monthly charge - accepts various formats and coerces to standard
 */
const lenientMonthlyChargeNodeSchema = z.object({
  fromDate: lenientDateString(''),
  amount: lenientNumber(0, MONTHLY_CHARGE_CONSTANTS.MIN_AMOUNT),
}).transform(val => ({
  fromDate: val.fromDate,
  amount: val.amount
}));

/**
 * Strict schema for monthly charge - enforces exact format
 */
const strictMonthlyChargeNodeSchema = z.object({
  fromDate: z.string().length(MONTHLY_CHARGE_CONSTANTS.DATE_FORMAT_LENGTH),
  amount: z.number().min(MONTHLY_CHARGE_CONSTANTS.MIN_AMOUNT),
}).transform(val => ({
  fromDate: val.fromDate,
  amount: val.amount
}));

// ============================================================================
// INTERFACE
// ============================================================================

export interface MonthlyChargeNode {
  fromDate: string;
  amount: number;
}

// ============================================================================
// NAMESPACE WITH METHODS
// ============================================================================

export namespace MonthlyChargeNode {
  /**
   * Safely deserialize unknown data into MonthlyChargeNode
   * Uses lenient parsing to handle various input formats
   */
  export function deserialize(data: unknown): MonthlyChargeNode {
    return lenientMonthlyChargeNodeSchema.parse(data);
  }

  /**
   * Create a new MonthlyChargeNode with validation
   * Uses strict parsing to ensure data integrity
   */
  export function create(data: MonthlyChargeNode): MonthlyChargeNode {
    return strictMonthlyChargeNodeSchema.parse(data);
  }

  /**
   * Validate that data conforms to MonthlyChargeNode structure
   */
  export function validate(data: unknown): data is MonthlyChargeNode {
    return strictMonthlyChargeNodeSchema.safeParse(data).success;
  }

  /**
   * Constants for validation and UI
   */
  export const Constants = MONTHLY_CHARGE_CONSTANTS;
}
